import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { AccreditationFooterProps } from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';

export function AccreditationFooter({
  heading,
  description,
  backgroundImages,
  formalDegreeKey,
  downloadBrochureButtonSlot = null,
}: {
  formalDegreeKey: FormalDegreeKey;
  downloadBrochureButtonSlot?: ReactNode;
} & AccreditationFooterProps) {
  const clientReady = useClientRender();
  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;

  function handleApplyNowClick() {
    trackEvent({
      type: 'click',
      name: 'Masters Page CTA Clicked',
      label: 'bottom_enroll_now',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
  }

  return (
    <Flex
      as='section'
      bg='#030304'
      flexDirection='column'
      justifyContent='center'
      minHeight={{ base: '500px', md: '560px' }}
      position='relative'
    >
      <Box display={{ base: 'none', md: 'block' }}>
        <Image
          alt=''
          quality={100}
          sizes='1440px'
          src={backgroundImages.desktop}
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          fill
        />
      </Box>

      <Box display={{ base: 'block', md: 'none' }}>
        <Image
          alt=''
          quality={100}
          sizes='1440px'
          src={backgroundImages.mobile}
          style={{
            objectFit: 'cover',
            objectPosition: 'center right',
          }}
          fill
        />
      </Box>
      <Flex alignItems='center' margin='0 auto' maxWidth='content-max-width' position='relative' width='100%'>
        <Flex direction='column' gap='48px' p='16px' width={{ base: '100%', md: '65%' }}>
          <Flex direction='column' gap='16px'>
            <Box display={{ base: 'none', md: 'block' }}>
              <Image alt='' height={88} quality={100} src='/images/accreditation/degree-logo.svg' width={88} />
            </Box>
            <Box display={{ base: 'block', md: 'none' }}>
              <Image alt='' height={64} quality={100} src='/images/accreditation/degree-logo.svg' width={64} />
            </Box>
            <Flex direction='column' gap='24px'>
              <Heading
                as='h2'
                color='white'
                fontWeight='500'
                maxW={{ base: '190px', md: 'unset' }}
                size={{ base: 'mobile-heading-2', md: 'heading-2' }}
              >
                {heading}
              </Heading>
              <Text color='grey.100'>{description}</Text>
            </Flex>
          </Flex>
          <Flex flexWrap='wrap' gap={{ base: '16px', md: '24px' }}>
            <ButtonLink
              buttonProps={{
                leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
                size: 'icon-left',
                variant: 'solid',
                colorScheme: 'blue',
                w: { base: '100%', md: 'fit-content' },
                onClick: handleApplyNowClick,
              }}
              linkProps={{
                href: hasActiveSubscription
                  ? `/checkout/individual/monthly/credit-card?degreeApplication=true&formalDegreeKey=${formalDegreeKey}`
                  : `/checkout/degree-application/purchase/credit-card?formalDegreeKey=${formalDegreeKey}`,
              }}
            >
              Enroll Now
            </ButtonLink>
            {downloadBrochureButtonSlot}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
